import type { GenericTemplatesRoutes } from '../types';

const BASE_PATH = `/api/v1/generic_templates`;
const DOCUMENT_BASE_PATH = `/sysconfig/api/v1/document_templates`;

export const getTokensUrl = (organizationId: number): string =>
  `${BASE_PATH}/organization/${organizationId}/template/tokens`;

export const getTemplateSectionsUrl = (organizationId: number, templateId: number): string =>
  `${BASE_PATH}/organization/${organizationId}/template/${templateId}/sections`;

export const getSectionConditionFieldsUrl = (organizationId: number): string =>
  `${BASE_PATH}/organization/${organizationId}/sections/condition_fields`;

export const getClaimTokenValuesUrl = (organizationId: number, claimId: number): string =>
  `${BASE_PATH}/organization/${organizationId}/claim/${claimId}/token_values`;

export const DOCUMENT_TEMPLATES_ROUTES: GenericTemplatesRoutes = {
  GET_TEMPLATES: (organizationId: number): string =>
    `${DOCUMENT_BASE_PATH}/organizations/${organizationId}/document_templates`,
  CREATE_TEMPLATE: (organizationId: number): string =>
    `${DOCUMENT_BASE_PATH}/organizations/${organizationId}/upload_document_template`,
  GET_TEMPLATE: (organizationId: number, template_id: number): string =>
    `${DOCUMENT_BASE_PATH}/organizations/${organizationId}/document_template/${template_id}/details`,
  GENERATE_FROM_TEMPLATE: ({
    organizationId,
    templateId,
    claimId,
    exposureId,
    recipientContactId,
    contactId,
    documentId,
    paymentRequestId,
  }: {
    organizationId: number;
    templateId: number;
    claimId: number;
    exposureId?: number;
    recipientContactId?: number | string;
    contactId?: number | string;
    documentId?: number | string;
    paymentRequestId?: number | string;
  }): string => {
    // TODO: Add query params in NGTPA-16670
    return `${organizationId}/${templateId}/${claimId}/${exposureId}/${recipientContactId}/${contactId}/${documentId}/${paymentRequestId}`;
  },
  UPDATE_TEMPLATE: (organizationId: number, templateId: number): string =>
    `${DOCUMENT_BASE_PATH}/organizations/${organizationId}/update_document_template/${templateId}`,
  UPDATE_TEMPLATE_IS_ENABLED: (organizationId: number, templateId: number): string =>
    `${DOCUMENT_BASE_PATH}/organizations/${organizationId}/document_template/${templateId}/is_enabled`,
  GENERATE_VIDEO_INVITATION: ({
    organizationId,
    claimId,
    communicationId,
  }: {
    organizationId: number;
    claimId: number;
    communicationId: number;
  }): string => `${organizationId}/${claimId}/${communicationId}`,
};

const MOI_EMAIL_BASE_PATH = (organizationId: number): string => `${BASE_PATH}/organization/${organizationId}/moi/email`;

// MOI email templates are served by a dedicated route surface (the shared generic-template routes
// reject moi_email). There is intentionally no delete route — MOI email templates cannot be deleted.
export const MOI_EMAIL_TEMPLATES_ROUTES = {
  GET_TEMPLATES: (organizationId: number): string => MOI_EMAIL_BASE_PATH(organizationId),
  GET_METHODS: (organizationId: number): string => `${MOI_EMAIL_BASE_PATH(organizationId)}/methods`,
  GET_TEMPLATE: (organizationId: number, templateId: number): string =>
    `${MOI_EMAIL_BASE_PATH(organizationId)}/${templateId}`,
  CREATE_TEMPLATE: (organizationId: number): string => MOI_EMAIL_BASE_PATH(organizationId),
  UPDATE_TEMPLATE: (organizationId: number, templateId: number): string =>
    `${MOI_EMAIL_BASE_PATH(organizationId)}/${templateId}`,
  UPDATE_TEMPLATE_IS_ENABLED: (organizationId: number, templateId: number): string =>
    `${MOI_EMAIL_BASE_PATH(organizationId)}/${templateId}`,
};

type DocumentRequestShareRouteParams = {
  organizationId: number;
  claimId: number;
  documentRequestId: number;
  contactId: number;
};

export const GENERIC_TEMPLATES_ROUTES: GenericTemplatesRoutes & {
  GENERATE_DOCUMENT_REQUEST_SHARE: (params: DocumentRequestShareRouteParams) => string;
} = {
  GET_TEMPLATES: (organizationId: number): string => `${BASE_PATH}/organization/${organizationId}`,
  CREATE_TEMPLATE: (organizationId: number): string => `${BASE_PATH}/organization/${organizationId}`,
  GET_TEMPLATE: (organizationId: number, template_id: number): string =>
    `${BASE_PATH}/organization/${organizationId}/template/${template_id}`,
  GENERATE_FROM_TEMPLATE: ({
    organizationId,
    templateId,
    claimId,
  }: {
    organizationId: number;
    templateId: number;
    claimId: number;
  }): string => {
    return `${BASE_PATH}/organization/${organizationId}/claim/${claimId}/template/${templateId}/generate_from_template`;
  },
  UPDATE_TEMPLATE: (organizationId: number, templateId: number): string =>
    `${BASE_PATH}/organization/${organizationId}/template/${templateId}`,
  UPDATE_TEMPLATE_IS_ENABLED: (organizationId: number, templateId: number): string =>
    `${BASE_PATH}/organization/${organizationId}/template/${templateId}`,
  GENERATE_VIDEO_INVITATION: ({
    organizationId,
    claimId,
    communicationId,
  }: {
    organizationId: number;
    claimId: number;
    communicationId: number;
  }): string =>
    `${BASE_PATH}/organization/${organizationId}/claim/${claimId}/generate_from_video_template/${communicationId}`,
  GENERATE_DOCUMENT_REQUEST_SHARE: ({
    organizationId,
    claimId,
    documentRequestId,
    contactId,
  }: DocumentRequestShareRouteParams): string =>
    `${BASE_PATH}/organization/${organizationId}/claim/${claimId}/generate_from_document_request_template/${documentRequestId}?contact_id=${contactId}`,
};
